/**
 * Library stored in the signed-in account on Supabase (tables + RLS from supabase/migrations).
 *
 * Same LibraryRepository interface as the localStorage version, so the store and the UI do not change.
 *  - load() reads the whole library in one call and remembers each row's revision.
 *  - save(lib) sends only what differs from the last loaded/saved copy, with the revision each row had.
 *    The server refuses the whole batch if any of those rows changed on another device (RepoError "conflict").
 *  - Ids, names, icons, colours, links, memo text, positions and dates are stored exactly as given.
 *  - The selected workspace is a per-device choice and stays in this browser.
 *  - Every request is tied to one account: a request is never sent with another account's token,
 *    and dispose() (log out / switch account) aborts what is still running.
 */
import { validateLibrary } from "../domain/backup";
import { nowIso } from "../domain/format";
import type { Action, Category, EntityKind, Library, OpenWith, Workspace } from "../domain/types";
import type { CloudConfig } from "../cloud/config";
import { RepoError } from "./errors";
import type { LibraryRepository, LoadResult } from "./repository";

type Row = Record<string, string | number | boolean>;
type Snap = Map<string, { row: Row; rev: number }>;
type Change =
  | { op: "upsert"; kind: EntityKind; id: string; rev: number; data: Row }
  | { op: "touch"; kind: EntityKind; id: string; updated_at: string }
  | { op: "delete"; kind: EntityKind; id: string; rev: number }
  | { op: "settings"; open_with: OpenWith };

const KINDS = [
  { kind: "workspace", key: "workspaces" },
  { kind: "category", key: "categories" },
  { kind: "action", key: "actions" }
] as const;

const TIMEOUT_MS = 20_000;

/** columns sent for each record, in a fixed order so two rows can be compared as strings */
function toRow(kind: EntityKind, r: Workspace | Category | Action): Row {
  const tail = { position: r.position, sample: r.sample === true, created_at: r.createdAt, updated_at: r.updatedAt };
  if (kind === "workspace") return { name: r.name, icon: r.icon, ...tail };
  if (kind === "category") { const c = r as Category; return { workspace_id: c.workspaceId, name: c.name, icon: c.icon, glow: c.glow, ...tail }; }
  const a = r as Action;
  return { category_id: a.categoryId, type: a.type, name: a.name, icon: a.icon, color: a.color, url: a.url, text: a.text, ...tail };
}
const same = (a: Row, b: Row) => JSON.stringify(a) === JSON.stringify(b);
const withoutUpdated = (r: Row) => { const { updated_at: _u, ...rest } = r; return rest; };
const iso = (v: unknown) => { const d = new Date(String(v)); return Number.isNaN(d.getTime()) ? v : d.toISOString(); };

export class CloudRepository implements LibraryRepository {
  readonly label: string;
  private snap: Record<EntityKind, Snap> = { workspace: new Map(), category: new Map(), action: new Map() };
  private openWith: OpenWith | null = null;
  private ctrl = new AbortController();
  private disposed = false;

  constructor(
    private cfg: CloudConfig,
    readonly userId: string,
    readonly email: string,
    private token: (userId: string) => Promise<string>,
    private storage: Storage = globalThis.localStorage
  ) {
    this.label = `บัญชี ${email} บน Cloud`;
  }

  /** stops everything still running for this account; later calls fail with "account" */
  dispose() { this.disposed = true; this.ctrl.abort(); }

  private get activeKey() { return `memolink-cloud:active:${this.userId}`; }

  /** Never throws: every failure (network, timeout, broken reply, bad data) comes back as status "error". */
  async load(): Promise<LoadResult> {
    try { return await this.loadOrThrow(); }
    catch (e) {
      const reason = e instanceof RepoError ? e.reason : "server";
      const msg = reason === "offline" ? "โหลดข้อมูลจาก Cloud ไม่ได้ ตรวจอินเทอร์เน็ตแล้วลองอีกครั้ง"
        : reason === "session" ? "เซสชันหมดอายุ เข้าสู่ระบบอีกครั้ง"
        : reason === "account" ? "บัญชีเปลี่ยนระหว่างโหลด"
        : "โหลดข้อมูลจาก Cloud ไม่ได้ (คำตอบไม่ถูกต้อง)";
      return { status: "error", error: msg };
    }
  }

  private async loadOrThrow(): Promise<LoadResult> {
    const raw = await this.call("POST", "rpc/load_library", { p_owner: this.userId }, { expect: "json" }) as any;
    if (!isLibraryReply(raw)) throw new RepoError("server", "load_library: unexpected reply");
    const camel = {
      workspaces: (raw.workspaces ?? []).map((w: any) => ({ id: w.id, name: w.name, icon: w.icon, position: w.position, ...(w.sample ? { sample: true } : {}), createdAt: iso(w.created_at), updatedAt: iso(w.updated_at) })),
      categories: (raw.categories ?? []).map((c: any) => ({ id: c.id, workspaceId: c.workspace_id, name: c.name, icon: c.icon, glow: c.glow, position: c.position, ...(c.sample ? { sample: true } : {}), createdAt: iso(c.created_at), updatedAt: iso(c.updated_at) })),
      actions: (raw.actions ?? []).map((a: any) => ({ id: a.id, categoryId: a.category_id, type: a.type, name: a.name, icon: a.icon, color: a.color, url: a.url, text: a.text, position: a.position, ...(a.sample ? { sample: true } : {}), createdAt: iso(a.created_at), updatedAt: iso(a.updated_at) }))
    };
    let active: string | null = null;
    try { active = this.storage.getItem(this.activeKey); } catch { /* blocked: first workspace */ }
    const v = validateLibrary(camel, { activeWorkspaceId: active, openWith: raw.settings?.open_with });
    if (!v.ok) return { status: "error", error: "ข้อมูลใน Cloud อ่านไม่ได้: " + v.errors[0] };

    const revs: Record<EntityKind, Map<string, number>> = { workspace: new Map(), category: new Map(), action: new Map() };
    (raw.workspaces ?? []).forEach((r: any) => revs.workspace.set(r.id, r.revision));
    (raw.categories ?? []).forEach((r: any) => revs.category.set(r.id, r.revision));
    (raw.actions ?? []).forEach((r: any) => revs.action.set(r.id, r.revision));
    for (const { kind, key } of KINDS) {
      this.snap[kind] = new Map((v.library[key] as (Workspace | Category | Action)[]).map(r => [r.id, { row: toRow(kind, r), rev: revs[kind].get(r.id) ?? 1 }]));
    }
    this.openWith = raw.settings ? v.library.settings.openWith : null;
    const lib = v.library;
    if (!lib.workspaces.length && !lib.categories.length && !lib.actions.length) return { status: "empty" };
    return { status: "ok", library: lib };
  }

  /** what has to be sent to make the cloud copy equal to lib, based on the last copy this device saw */
  private diff(lib: Library): Change[] {
    const ups: Change[] = [], dels: Change[] = [];
    for (const { kind, key } of KINDS) {
      const seen = new Set<string>();
      for (const r of lib[key] as (Workspace | Category | Action)[]) {
        seen.add(r.id);
        const row = toRow(kind, r), old = this.snap[kind].get(r.id);
        if (!old) ups.push({ op: "upsert", kind, id: r.id, rev: 0, data: row });
        else if (same(row, old.row)) continue;
        else if (same(withoutUpdated(row), withoutUpdated(old.row))) ups.push({ op: "touch", kind, id: r.id, updated_at: String(row.updated_at) });
        else ups.push({ op: "upsert", kind, id: r.id, rev: old.rev, data: row });
      }
      for (const [id, old] of this.snap[kind]) if (!seen.has(id)) dels.push({ op: "delete", kind, id, rev: old.rev });
    }
    dels.reverse(); // actions first, then categories, then workspaces
    const out = [...ups, ...dels];
    if (lib.settings.openWith !== (this.openWith ?? "default")) out.push({ op: "settings", open_with: lib.settings.openWith });
    return out;
  }

  async save(lib: Library): Promise<void> {
    if (this.disposed) throw new RepoError("account");
    const changes = this.diff(lib);
    if (changes.length) {
      const revs = await this.call("POST", "rpc/apply_changes", { p_owner: this.userId, p_changes: changes }, { expect: "json" });
      if (this.disposed) throw new RepoError("account");
      // the reply must name every saved row with its new revision before the local copy is updated;
      // anything else means the outcome is unknown, so nothing is marked as saved and a retry re-sends the same batch
      if (!isRevisionReply(revs, changes)) throw new RepoError("offline", "apply_changes: unexpected reply");
      for (const c of changes) {
        if (c.op === "settings") { this.openWith = c.open_with; continue; }
        const snap = this.snap[c.kind];
        if (c.op === "delete") { snap.delete(c.id); continue; }
        const rec = (lib[KINDS.find(k => k.kind === c.kind)!.key] as (Workspace | Category | Action)[]).find(r => r.id === c.id)!;
        snap.set(c.id, { row: toRow(c.kind, rec), rev: revs[`${c.kind}:${c.id}`] as number });
      }
    }
    try {
      if (lib.settings.activeWorkspaceId) this.storage.setItem(this.activeKey, lib.settings.activeWorkspaceId);
    } catch { /* only a convenience */ }
  }

  /**
   * Deletes the whole library in one server transaction, only if the account holds exactly the rows (and revisions)
   * this device last saw. A row added, changed or removed on another device meanwhile → "conflict", nothing deleted.
   */
  async clear(): Promise<void> {
    if (this.disposed) throw new RepoError("account");
    const expected: Record<EntityKind, Record<string, number>> = { workspace: {}, category: {}, action: {} };
    for (const { kind } of KINDS) for (const [id, s] of this.snap[kind]) expected[kind][id] = s.rev;
    const n = await this.call("POST", "rpc/clear_library", { p_owner: this.userId, p_expected: expected }, { expect: "json" });
    if (this.disposed) throw new RepoError("account");
    if (typeof n !== "number" || !Number.isInteger(n) || n < 0) throw new RepoError("offline", "clear_library: unexpected reply");
    for (const { kind } of KINDS) this.snap[kind] = new Map();
  }

  async saveSafetyCopy(lib: Library, reason: string) {
    await this.call("POST", "safety_copies?on_conflict=user_id",
      { user_id: this.userId, saved_at: nowIso(), reason, library: lib }, { prefer: "resolution=merge-duplicates,return=minimal", expect: "none" });
  }
  async loadSafetyCopy() {
    try {
      const rows = await this.call("GET", `safety_copies?select=saved_at,reason,library&user_id=eq.${this.userId}`, undefined, { expect: "json" });
      if (!Array.isArray(rows)) return null;
      const o = rows[0]; if (!o) return null;
      const v = validateLibrary(o.library, o.library?.settings);
      return v.ok ? { library: v.library, savedAt: String(iso(o.saved_at)), reason: String(o.reason) } : null;
    } catch { return null; }
  }
  async clearSafetyCopy() {
    try { await this.call("DELETE", `safety_copies?user_id=eq.${this.userId}`, undefined, { prefer: "return=minimal", expect: "none" }); } catch { /* ignore */ }
  }

  /**
   * One REST call as this account; maps every failure to a RepoError reason.
   * The timeout and the log-out abort cover the whole exchange, up to the last byte of the reply body.
   * expect "json": a 2xx reply must carry valid JSON (a missing, cut or broken body is an error, never null).
   * expect "none": for Prefer return=minimal; the body is still read to the end, and may be empty.
   */
  private async call(method: string, path: string, body: unknown, opts: { prefer?: string; expect: "json" | "none" }): Promise<unknown> {
    if (this.disposed) throw new RepoError("account");
    const token = await this.token(this.userId);
    if (this.disposed) throw new RepoError("account");
    const timeout = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; timeout.abort(); }, TIMEOUT_MS);
    const stop = () => timeout.abort();
    this.ctrl.signal.addEventListener("abort", stop);
    const lost = () => new RepoError(this.disposed ? "account" : "offline", timedOut ? "timeout" : "connection lost");
    let res: Response, text: string;
    try {
      try {
        res = await fetch(`${this.cfg.url}/rest/v1/${path}`, {
          method,
          headers: {
            apikey: this.cfg.anonKey,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(opts.prefer ? { Prefer: opts.prefer } : {})
          },
          body: body === undefined ? undefined : JSON.stringify(body),
          signal: timeout.signal,
          cache: "no-store"
        });
      } catch { throw lost(); }
      if (this.disposed) throw new RepoError("account");
      // reading the body can hang or fail too; it races the same timeout / log-out abort
      try {
        text = await new Promise<string>((resolve, reject) => {
          if (timeout.signal.aborted) { reject(new Error("aborted")); return; }
          timeout.signal.addEventListener("abort", () => reject(new Error("aborted")), { once: true });
          res.text().then(resolve, reject);
        });
      } catch { throw lost(); }
    } finally {
      clearTimeout(timer);
      this.ctrl.signal.removeEventListener("abort", stop);
    }
    if (this.disposed) throw new RepoError("account");

    if (res.ok) {
      if (opts.expect === "none") return null;
      if (!text.trim()) throw new RepoError("offline", "empty reply");
      try { return JSON.parse(text); }
      catch { throw new RepoError("offline", "broken JSON reply"); } // outcome unknown: treat like a lost reply
    }
    let json: any = null;
    try { json = text ? JSON.parse(text) : null; } catch { /* error reply without JSON: decided by status below */ }
    const code = String(json?.code ?? "");
    if (res.status === 401 || code === "MLAUT" || code === "PGRST301" || code === "PGRST303") throw new RepoError("session", json?.message);
    if (code === "MLACC") throw new RepoError("account", json?.message);
    if (code === "MLCON" || code === "23503" || code === "23505") throw new RepoError("conflict", json?.message, json?.details);
    if (res.status >= 500 || res.status === 0) throw new RepoError("offline", json?.message || `HTTP ${res.status}`);
    throw new RepoError("server", json?.message || `HTTP ${res.status}`);
  }
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const rowsOk = (v: unknown) => Array.isArray(v) && v.every(r => isObj(r) && typeof r.id === "string" && Number.isInteger(r.revision));
/** shape of load_library(): three arrays of rows with id + revision, and settings (object or null) */
function isLibraryReply(v: unknown): boolean {
  return isObj(v) && rowsOk(v.workspaces) && rowsOk(v.categories) && rowsOk(v.actions) && (v.settings === null || v.settings === undefined || isObj(v.settings));
}
/** shape of apply_changes(): a revision (integer) for every saved row, null for every deleted row */
function isRevisionReply(v: unknown, changes: Change[]): v is Record<string, number | null> {
  if (!isObj(v)) return false;
  return changes.every(c => {
    if (c.op === "settings") return true;
    const r = v[`${c.kind}:${c.id}`];
    return c.op === "delete" ? r === null : Number.isInteger(r);
  });
}

/** used while nobody is signed in: nothing to load, nothing can be saved */
export class SignedOutRepository implements LibraryRepository {
  readonly label = "ยังไม่ได้เข้าสู่ระบบ";
  async load(): Promise<LoadResult> { return { status: "empty" }; }
  async save(): Promise<void> { throw new RepoError("account"); }
  async clear(): Promise<void> { throw new RepoError("account"); }
  async saveSafetyCopy(): Promise<void> { throw new RepoError("account"); }
  async loadSafetyCopy() { return null; }
  async clearSafetyCopy() { /* nothing */ }
}
